import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Form,
  Input,
  DatePicker,
  message,
  Card,
  Modal,
  Pagination,
} from "antd";
import {
  SearchOutlined,
  LogoutOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { LoginRecordService } from "../../services";
import { LoginRecord, LoginRecordQueryParams } from "../../types/loginRecord";
import ErrorHandler from "../../utils/errorHandler";
import "./LoginRecord.scss";

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const LoginRecordList: React.FC = () => {
  const [records, setRecords] = useState<LoginRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchForm] = Form.useForm();

  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 20,
    total: 0,
  });

  // 查询参数
  const [searchParams, setSearchParams] = useState<LoginRecordQueryParams>({});

  // 加载登录记录
  const loadRecords = async (params?: LoginRecordQueryParams) => {
    setLoading(true);
    try {
      const queryParams = {
        ...searchParams,
        ...params,
        page: pagination.current,
        limit: pagination.limit,
      };

      const response = await LoginRecordService.getLoginRecords(queryParams);

      if (response.code === 0 && response.data) {
        const data = response.data;
        setRecords(data.records || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取登录记录失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadRecords();
  }, [pagination.current, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  // 搜索登录记录
  const handleSearch = async (values: any) => {
    const { dateRange, ...rest } = values;

    const params: LoginRecordQueryParams = {
      ...rest,
      page: 1, // 搜索时重置到第一页
    };

    // 处理日期范围
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }

    setSearchParams(params);
    setPagination((prev) => ({ ...prev, current: 1 }));
    await loadRecords(params);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    const resetParams = {};
    setSearchParams(resetParams);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 使用临时的空参数对象，绕过状态合并问题
    loadRecordsWithParams({ page: 1, limit: pagination.limit });
  };

  // 直接使用参数加载记录，不合并状态
  const loadRecordsWithParams = async (params: any) => {
    setLoading(true);
    try {
      const response = await LoginRecordService.getLoginRecords(params);

      if (response.code === 0 && response.data) {
        const data = response.data;
        setRecords(data.records || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取登录记录失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 踢下线
  const handleKickout = (record: LoginRecord) => {
    // 获取用户手机号，如果嵌套对象不存在则使用默认值
    const phoneNumber = record.account?.phone || "未知用户";

    confirm({
      title: "确认踢下线",
      icon: <ExclamationCircleOutlined />,
      content: `确定要将用户 ${phoneNumber} (${record.ipAddress}) 踢下线吗？`,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await LoginRecordService.kickout(record.id);

          if (response.code === 0) {
            message.success("已成功踢下线");
            loadRecords();
          } else {
            message.error(response.msg || "操作失败");
          }
        } catch (error) {
          ErrorHandler.handleApiError(error);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 分页变化
  const handlePaginationChange = (page: number, limit?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      limit: limit || prev.limit,
    }));
  };

  // 渲染登录方式
  const renderLoginMethod = (method: "sms" | "password") => {
    const methodMap: Record<string, string> = {
      sms: "短信验证码",
      password: "账号密码",
    };
    return methodMap[method] || method;
  };

  // 安全获取嵌套属性
  const getNestedValue = (
    record: any,
    path: string,
    defaultValue: string = "-"
  ) => {
    try {
      const keys = path.split(".");
      let value = record;

      for (const key of keys) {
        if (value === undefined || value === null) return defaultValue;
        value = value[key];
      }

      return value || defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "手机号",
      key: "account.phone",
      width: 120,
      render: (text: any, record: LoginRecord) =>
        getNestedValue(record, "account.phone", "未知用户"),
    },
    {
      title: "IP地址",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 120,
    },
    {
      title: "操作系统",
      dataIndex: "operatingSystem",
      key: "operatingSystem",
      width: 200,
    },
    {
      title: "浏览器",
      dataIndex: "browserName",
      key: "browserName",
      width: 120,
    },
    {
      title: "登录方式",
      dataIndex: "loginMethod",
      key: "loginMethod",
      width: 100,
      render: renderLoginMethod,
    },
    {
      title: "登录时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
    },
    {
      title: "登录过期时间",
      dataIndex: "tokenExpiresAt",
      key: "tokenExpiresAt",
      width: 180,
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (_: any, record: LoginRecord) => (
        <Space>
          {record.isActive && (
            <Button
              type="link"
              danger
              size="small"
              icon={<LogoutOutlined />}
              onClick={() => handleKickout(record)}
            >
              踢下线
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="login-record-list">
      {/* 搜索区域 */}
      <Card className="search-card" style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="accountId" label="账号ID">
            <Input placeholder="请输入账号ID" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="phone" label="手机号">
            <Input placeholder="请输入手机号" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="dateRange" label="登录时间">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
              >
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 登录记录列表 */}
      <Card title="登录记录" className="record-table-card">
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1000 }}
        />

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.limit}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }
            onChange={handlePaginationChange}
          />
        </div>
      </Card>
    </div>
  );
};

export default LoginRecordList;
